import React from 'react';
import i18n from 'i18n-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { threadIdPreview } from './smartAssistUtils';
import type { ConversationEntry } from './types';

export interface SmartAssistChatSidebarProps {
  sidebarRows: ConversationEntry[];
  activeSidebarThreadId: string | null;
  deletingConvId: string | null;
  onOpen(entry: ConversationEntry): void;
  onDelete(conversationId: string): void;
}

export function SmartAssistChatSidebar({
  sidebarRows,
  activeSidebarThreadId,
  deletingConvId,
  onOpen,
  onDelete,
}: SmartAssistChatSidebarProps): React.ReactElement {
  return (
    <aside className="smartAssist__chat-sidebar">
      <div className="smartAssist__chat-sidebar-title">
        {i18n.translate('smartAssist.previousConversations')}
      </div>
      <div className="smartAssist__chat-sidebar-list">
        {sidebarRows.length === 0 ? (
          <div className="smartAssist__chat-sidebar-empty">
            {i18n.translate('smartAssist.noRecentConversations')}
          </div>
        ) : (
          sidebarRows.map((c) => {
            const conversationId = c.conversationId;
            const isActive =
              Boolean(activeSidebarThreadId) &&
              Boolean(conversationId) &&
              String(conversationId) === String(activeSidebarThreadId);
            const isDeleting = Boolean(deletingConvId) && String(deletingConvId) === String(conversationId);
            return (
              <div
                key={c.id}
                className={clsx(
                  'smartAssist__chat-sidebar-item',
                  isActive && 'smartAssist__chat-sidebar-item--active',
                )}
              >
                <button
                  type="button"
                  className="smartAssist__chat-sidebar-item-trigger"
                  title={conversationId}
                  aria-current={isActive ? 'true' : undefined}
                  onClick={() => {
                    onOpen(c);
                  }}
                >
                  <FontAwesomeIcon icon={['fas', 'clock-rotate-left']} className="smartAssist__chat-sidebar-icon" />
                  <span className="smartAssist__chat-sidebar-content">
                    <span className="smartAssist__chat-sidebar-text">{c.text}</span>
                    <span className="smartAssist__chat-sidebar-meta">
                      <span className="smartAssist__chat-sidebar-date">{c.date}</span>
                      {conversationId && (
                        <span className="smartAssist__chat-sidebar-id">
                          {threadIdPreview(conversationId)}
                        </span>
                      )}
                    </span>
                  </span>
                </button>
                {conversationId && (
                  <button
                    type="button"
                    className="smartAssist__chat-sidebar-delete"
                    aria-label={i18n.translate('smartAssist.deleteConversationAria') as string}
                    disabled={!!isDeleting}
                    onClick={() => {
                      onDelete(conversationId);
                    }}
                  >
                    {isDeleting ? (
                      <span className="smartAssist__thinking-dots" aria-hidden="true">
                        <span className="smartAssist__thinking-dot" />
                        <span className="smartAssist__thinking-dot" />
                        <span className="smartAssist__thinking-dot" />
                      </span>
                    ) : (
                      <FontAwesomeIcon icon={['fas', 'trash']} />
                    )}
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>
    </aside>
  );
}
